import React, { useEffect, useRef, useState } from "react";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRangePicker } from "react-date-range";
import {
	addDays,
	endOfDay,
	startOfDay,
	startOfMonth,
	endOfMonth,
	addMonths,
	startOfWeek,
	endOfWeek,
	isSameDay,
} from "date-fns";

const DateRangePickerComponent = ({ onSelect, minDate=null, dateRange }) => {
	// Function to format date to "DD Mon YYYY"
	const [shownDate, setShownDate] = useState(new Date());
	const formatDate = (date) => {
		const day = date.getDate();
		const month = date.toLocaleString("default", { month: "short" });
		const year = date.getFullYear();
		return `${month} ${day}, ${year}`;
	};
	// Create a function to get current defineds dynamically
	const getDefineds = () => {
		const now = new Date();
		return {
			startOfWeek: startOfWeek(now),
			endOfWeek: endOfWeek(now),
			startOfLastWeek: startOfWeek(addDays(now, -7)),
			endOfLastWeek: endOfWeek(addDays(now, -7)),
			startOfToday: startOfDay(now),
			endOfToday: endOfDay(now),
			startOfYesterday: startOfDay(addDays(now, -1)),
			endOfYesterday: endOfDay(addDays(now, -1)),
			startOfMonth: startOfMonth(now),
			endOfMonth: endOfMonth(now),
			startOfLastMonth: startOfMonth(addMonths(now, -1)),
			endOfLastMonth: endOfMonth(addMonths(now, -1)),
		};
	};
	const calendarRef = useRef(null);
	const [maxDate, setMaxDate] = useState(new Date());
	const [selectionRange, setSelectionRange] = useState({
		startDate: dateRange.startDate,
		endDate: dateRange.endDate,
		key: "selection",
	});
	const [displaySelectionRange, setDisplaySelectionRange] = useState({
		startDate: dateRange.startDate,
		endDate: dateRange.endDate,
		key: "selection",
	});
	const [showCalendar, setShowCalendar] = useState(false);
	const [focusedRange, setFocusedRange] = useState([0, 0]);

	useEffect(() => {
		setSelectionRange({
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			key: "selection",
		});
		setDisplaySelectionRange({
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			key: "selection",
		});
	}, [dateRange.startDate, dateRange.endDate]);

	useEffect(() => {
		let newMaxDate = new Date(selectionRange.startDate),
			maxDate = newMaxDate,
			currentDate = new Date();
		newMaxDate.setMonth(newMaxDate.getMonth() + 2);
		if (newMaxDate.getDate() + 60 > currentDate.getDate()) {
			maxDate = currentDate;
			setMaxDate(currentDate);
		} else {
			setMaxDate(newMaxDate);
		}
	}, [selectionRange.startDate]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (calendarRef.current && !calendarRef.current.contains(event.target)) {
				setShowCalendar(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleSelect = (ranges) => {
		setSelectionRange(ranges.selection);
		if (ranges.selection.startDate && ranges.selection.endDate) {
		}
	};

	const handleRangeFocusChange = (ranges) => {
		setFocusedRange(ranges.focusedRange);
	};

	const toggleCalendar = () => {
		setShowCalendar(!showCalendar);
		setSelectionRange(displaySelectionRange);
		// Reset focused range when opening calendar
		if (!showCalendar) {
			setFocusedRange([0, 0]);
		}
	};

	const generateReport = () => {
		setDisplaySelectionRange(selectionRange);
		setShowCalendar(false);
		onSelect(selectionRange);
	};

	const staticRangeHandler = {
		range: {},
		isSelected(range) {
			const definedRange = this.range();
			return (
				isSameDay(range.startDate, definedRange.startDate) &&
				isSameDay(range.endDate, definedRange.endDate)
			);
		},
	};

	const createStaticRanges = (ranges) => {
		return ranges.map((range) => ({ ...staticRangeHandler, ...range }));
	};

	const defaultStaticRanges = createStaticRanges([
		{
			label: "Today",
			range: () => {
				const defineds = getDefineds();
				return {
					startDate: defineds.startOfToday,
					endDate: defineds.endOfToday,
				};
			},
		},
		{
			label: "Yesterday",
			range: () => {
				const defineds = getDefineds();
				return {
					startDate: defineds.startOfYesterday,
					endDate: defineds.endOfYesterday,
				};
			},
		},
		{
			label: "This Week",
			range: () => {
				const defineds = getDefineds();
				const today = new Date();
				return {
					startDate: defineds.startOfWeek,
					endDate: today,
				};
			},
		},
		{
			label: "Last Week",
			range: () => {
				const defineds = getDefineds();
				return {
					startDate: defineds.startOfLastWeek,
					endDate: defineds.endOfLastWeek,
				};
			},
		},
		{
			label: "This Month",
			range: () => {
				const defineds = getDefineds();
				const today = new Date();
				return {
					startDate: defineds.startOfMonth,
					endDate: today,
				};
			},
		},
		{
			label: "Last Month",
			range: () => {
				const defineds = getDefineds();
				return {
					startDate: defineds.startOfLastMonth,
					endDate: defineds.endOfLastMonth,
				};
			},
		},
	]);

	return (
		<div className="dateRangePickerOuter">
			<button id="dateRange" onClick={()=>toggleCalendar()}>
				{formatDate(displaySelectionRange.startDate)} -{" "}
				{formatDate(displaySelectionRange.endDate)}
			</button>
			{showCalendar && (
				<div ref={calendarRef} className="dateRangePicketInner">
					<DateRangePicker
						ranges={[selectionRange]}
						onChange={handleSelect}
						onRangeFocusChange={handleRangeFocusChange}
						focusedRange={focusedRange}
						style={{ flexGrow: 1 }}
						minDate={minDate??new Date("2024-01-01")}
						maxDate={maxDate}
						moveRangeOnFirstSelection={true}
						retainEndDateOnFirstSelection={true}
						inputRanges={[]}
						staticRanges={[...defaultStaticRanges]}
						showMonthAndYearPickers={true}
						showDateDisplay={false}
						showPreview={true}
					/>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: "0.5rem",
						}}
					>
						<div>
							<button
								type="button"
								className="btn btn-primary"
								onClick={() => generateReport()}
								style={{
									backgroundColor: "rgb(41 57 78)",
									borderColor: "rgb(41 57 78)",
									color: "#fff",
								}}
							>
								Apply
							</button>
						</div>
						<div>
							<button
								type="button"
								className="btn btn-light"
								onClick={() => toggleCalendar()}
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DateRangePickerComponent;
